import { qk } from './queryKeys'

/**
 * doctype → the query-key prefixes invalidated by a realtime event for it
 * (`PLAN_TRACK_C.md` C1.4b).
 *
 * A `list_update` is per-doctype, so the prefixes are coarse domain roots; the
 * handler filters by `predicate`. This file is the **only** place a doctype is
 * mapped to keys — `grep -n "list_update\|doc_update" src` finds the handler in
 * `realtime.ts` and nothing else.
 */

/** doctype → query-key prefixes a `list_update` invalidates. */
export const realtimeMap: Record<string, readonly (readonly unknown[])[]> = {
  'Salon Appointment': [qk.appointmentsRoot, qk.frontDeskRoot, qk.customersRoot],
  'Sales Invoice': [qk.financeRoot, qk.receiptRoot],
  'Payment Entry': [qk.financeRoot, qk.receiptRoot],
  Customer: [qk.customersRoot, qk.posRoot],
  Employee: [qk.staffRoot, qk.posRoot],
  Item: [qk.catalogRoot, qk.posRoot],
  'Item Price': [qk.catalogRoot, qk.posRoot],
  'Item Group': [qk.lookupRoot('itemGroups')],
  Company: [qk.companiesRoot, qk.branchesRoot],
  Warehouse: [qk.storesRoot, qk.inventoryRoot],
  'Stock Entry': [qk.inventoryRoot],
  'Stock Ledger Entry': [qk.inventoryRoot],
  'Leave Application': [qk.staffRoot],
}

/**
 * doctype → the **one** doc key a `doc_update` invalidates, by name.
 *
 * C1.4e: never invalidate a whole domain for a single document. A doctype with
 * no cached doc key (an `Item`, a `Warehouse`, a `Stock Ledger Entry`) has
 * nothing to invalidate here — its list screens are refreshed by the
 * per-doctype `list_update` Frappe publishes alongside the doc event
 * (`frappe/model/document.py:1231`).
 */
export const realtimeDocKeys: Record<
  string,
  (name: string) => readonly unknown[]
> = {
  'Salon Appointment': (name) => qk.appointment(name),
  'Sales Invoice': (name) => qk.receipt(name),
  Customer: (name) => qk.customer(name),
  Employee: (name) => qk.staffMember(name),
}
